import { getExtension, getExtensionValue, parseReference } from '@medplum/core';
import type { Task } from '@medplum/fhirtypes';
import { CODE_SYSTEMS, EXTENSIONS, LEGACY_TASK_CATEGORY_SYSTEM, TIME_PERIODS } from './constants';
import { SCRIPTSURE_PHARMACY_MESSAGE_SYSTEMS } from './identifiers';
import { LOINC_DISPLAY_NAMES_ES, SURVEY_SCORES_DISPLAY_NAMES_ES } from './loincLabDisplayNames';
import type {
  CarePlanItemKind,
  LifestyleTaskCategoryCode,
  MeasurementTaskCategoryCode,
  MedicationTaskCategoryCode,
  TaskCategoryCode,
  TaskOwnerType,
  TimingRepeat,
} from './types';
import {
  LIFESTYLE_TASK_CATEGORY_CODES,
  MEASUREMENT_TASK_CATEGORY_CODES,
  MEDICATION_TASK_CATEGORY_CODES,
  TASK_CATEGORY_CODES,
} from './types';

const SURVEY_SCORES_SYSTEM = 'https://liza.health/fhir/CodeSystem/survey-scores';
const TASK_INPUT_KEYS_SYSTEM = 'https://liza.health/fhir/CodeSystem/task-input-keys';

/**
 * Systems a result Task/Observation `code` may use: a real total-score LOINC (PHQ-9, GAD-7, and every
 * shipped lifestyle metric) or a minted `survey-scores` code for instruments without a LOINC (Mood).
 * Used to identify result/measurement resources regardless of system — kept in sync with
 * medplum-config's CodeSystem definitions. The `lifestyle-measurements` system isn't listed because
 * no shipped metric uses it yet; #5972 adds it here when it introduces minted metrics.
 *
 * This is the single source of truth: UI (`@liza/ui`) and services both consume it (via
 * `isResultCodeSystem`) instead of maintaining their own copies, so adding a system here lights it
 * up everywhere — the day-view reporting sheet, the care-plan results list, and task auto-completion.
 */
export const RESULT_CODE_SYSTEMS: readonly string[] = [CODE_SYSTEMS.LOINC, SURVEY_SCORES_SYSTEM];

/** True when `system` is one a result/measurement code lives on (LOINC or a minted result system). */
export function isResultCodeSystem(system: string | undefined): boolean {
  return !!system && RESULT_CODE_SYSTEMS.includes(system);
}

/** Extract ownerType ('patient' | 'care_team') from a Task's OWNER_TYPE extension. */
export function getOwnerType(task: Task): TaskOwnerType | null {
  const value = getExtensionValue(task, EXTENSIONS.OWNER_TYPE) as string | undefined;
  if (value === 'patient' || value === 'care_team') return value;
  return null;
}

/**
 * Whether a Task is the care team's to act on.
 *
 * Two ways to qualify, because we are not the only author of Tasks in a patient's compartment:
 *
 *  - `owner-type: care_team` — our own convention, set by `buildTaskBody` on everything we write.
 *  - a `Practitioner` owner with no `owner-type` at all — an externally-authored Task that names a
 *    clinician. ScriptSure's webhook writes pharmacy messages (`RxChangeRequest`, `RxRenewalRequest`,
 *    transmission errors) this way: assigned to the prescriber, but with no `owner-type`, since the
 *    extension is proprietary to us and the vendor knows nothing of it.
 *
 * Requiring the `Practitioner/` prefix is load-bearing rather than a general "has an owner" check. A
 * patient-owned Task carries `owner` too — pointing at the `Patient` — so accepting any owner would
 * surface exactly the tasks issue #7325 excluded (Liza's to chase, not the practitioner's).
 *
 * Shared so every surface listing care-team work agrees on what counts: the Home worklist's
 * `practitionerTaskProvider` and the care-plan rail's Worklist tab (via `listTasks`) both read this.
 * They drifted before it existed — the vendor's tasks were invisible on both, for the same reason.
 */
export function isCareTeamTask(task: Task): boolean {
  const ownerType = getOwnerType(task);
  if (ownerType) return ownerType === 'care_team';
  return !!task.owner?.reference?.startsWith('Practitioner/');
}

/**
 * Whether this Task is a pharmacy message ScriptSure's webhook wrote — a refill request, a
 * transmission error, and friends.
 *
 * Keyed on the vendor's own `message-type` coding rather than anything of ours, because none of our
 * task conventions apply to a resource the vendor authored: `getTaskCategory` returns `null` (no
 * `liza-task-category` input) and `getOwnerType` returns `null` (the extension is proprietary to us).
 *
 * Lives here beside `isCareTeamTask` rather than in `@liza/services` so both can share it —
 * `taskUtils` cannot import from services, and the API and UI must agree on what counts. The vendor
 * owns every field on these Tasks, so this predicate is what gates both the launch affordance and the
 * write guard that keeps a clinician-facing update from rewriting them.
 */
export function isPharmacyMessageTask(task: Pick<Task, 'code'>): boolean {
  return !!task.code?.coding?.some((c) => c.system === SCRIPTSURE_PHARMACY_MESSAGE_SYSTEMS.MESSAGE_TYPE);
}

/** The vendor's message type (`RxRenewalRequest`, `NewRx`, …), or null for a non-pharmacy Task. */
export function getPharmacyMessageType(task: Pick<Task, 'code'>): string | null {
  const coding = task.code?.coding?.find((c) => c.system === SCRIPTSURE_PHARMACY_MESSAGE_SYSTEMS.MESSAGE_TYPE);
  return coding?.code ?? null;
}

/**
 * A pharmacy message's vendor status, from `Task.businessStatus` — `Pending`, `Error`, and friends.
 * Prefers the coded value and falls back to `text`, which the vendor sets to a longer phrasing
 * ("Prescription transmission error") than the code's display.
 */
export function getPharmacyMessageStatus(task: Pick<Task, 'businessStatus'>): string | null {
  const coding = task.businessStatus?.coding?.find(
    (c) => c.system === SCRIPTSURE_PHARMACY_MESSAGE_SYSTEMS.MESSAGE_STATUS,
  );
  return coding?.code ?? task.businessStatus?.text ?? null;
}

/**
 * Statuses that mean a pharmacy-message Task is finished, whichever side finished it. The same five
 * Medplum's provider app treats as closed when deciding whether a message can still be acted on.
 */
export const PHARMACY_MESSAGE_TERMINAL_STATUSES: readonly Task['status'][] = [
  'completed',
  'cancelled',
  'rejected',
  'failed',
  'entered-in-error',
];

/**
 * What Liza can do to resolve a pharmacy message, decided from the Task alone.
 *
 * - `acknowledge` — a `NewRx` transmission error that is still open. The one resolution the vendor
 *   honours from our side: the bot's `acknowledge` action sets the message to `Error Reviewed` at
 *   ScriptSure and completes the Task, so the two systems agree.
 * - `vendor-only` — every other open message (refill and change requests, network errors). Approving,
 *   denying or answering one happens inside the vendor's widget; completing the Task locally would
 *   show it done here while the pharmacy still waits.
 * - `closed` — already at a terminal status; nothing left to resolve.
 *
 * Mirrors the gate in Medplum's provider app (`supportsErrorAcknowledgment`), which is the only
 * type-specific action it offers. A message the vendor can no longer see at all (aged out of its
 * retrieval window) is not knowable from the Task and is handled where the launch fails.
 */
export type PharmacyMessageResolution = { kind: 'acknowledge' } | { kind: 'vendor-only' } | { kind: 'closed' };

export function getPharmacyMessageResolution(
  task: Pick<Task, 'code' | 'businessStatus' | 'status'>,
): PharmacyMessageResolution {
  if (PHARMACY_MESSAGE_TERMINAL_STATUSES.includes(task.status)) return { kind: 'closed' };
  const isTransmissionError = getPharmacyMessageType(task) === 'NewRx' && getPharmacyMessageStatus(task) === 'Error';
  return isTransmissionError ? { kind: 'acknowledge' } : { kind: 'vendor-only' };
}

/**
 * A pharmacy message's `input[]` detail rows, keyed by the vendor's bare `type.text`.
 *
 * Deliberately not routed through the `TASK_INPUT_KEYS_SYSTEM` readers: the vendor writes these with
 * no coding system at all, so a system-keyed lookup finds nothing.
 */
export function getPharmacyMessageInput(task: Pick<Task, 'input'>, key: string): string | null {
  const entry = task.input?.find((i) => i.type?.text === key);
  return (entry?.valueString as string | undefined) ?? null;
}

/** Extract task category (clinical, food, movement, mind) from the Task's input array. */
export function getTaskCategory(task: Task): TaskCategoryCode | null {
  if (!task.input) return null;
  const validCategories = new Set<string>(TASK_CATEGORY_CODES);
  for (const inp of task.input) {
    const isCategoryMarker = inp.type?.coding?.some(
      (c) => c.system === LEGACY_TASK_CATEGORY_SYSTEM && c.code === LEGACY_TASK_CATEGORY_SYSTEM,
    );
    if (isCategoryMarker) {
      const val = inp.valueString as string | undefined;
      if (val && validCategories.has(val)) return val as TaskCategoryCode;
    }
  }
  return null;
}

/** Extract the result code from a task's code.coding array — a total-score LOINC or a minted
 * `survey-scores` code. Returns null if the task carries no result coding (i.e. not a measurement). */
export function getTaskLoincCode(task: Task): string | null {
  return task.code?.coding?.find((c) => isResultCodeSystem(c.system))?.code ?? null;
}

/** Extract the result code's display name from a task's code.coding array (LOINC or `survey-scores`).
 * Returns null if no result coding found. */
export function getTaskLoincDisplay(task: Task): string | null {
  return task.code?.coding?.find((c) => isResultCodeSystem(c.system))?.display ?? null;
}

/** True when a Task is one of the lifestyle-change category codes. */
export function isLifestyleTask(task: Task): boolean {
  const category = getTaskCategory(task);
  return category !== null && LIFESTYLE_TASK_CATEGORY_CODES.includes(category as LifestyleTaskCategoryCode);
}

/**
 * True when a Task carries the `measurement` task category — i.e. a clinical result (survey, lab,
 * vital) that renders in the Results section. Distinct from `isMeasurableTask`: a lifestyle
 * measurement is measurable but carries its lifestyle-domain category, not `measurement`.
 */
export function isMeasurementTask(task: Task): boolean {
  const category = getTaskCategory(task);
  return category !== null && MEASUREMENT_TASK_CATEGORY_CODES.includes(category as MeasurementTaskCategoryCode);
}

/**
 * True when a Task is a tracked quantitative metric — it carries a result code (LOINC or a minted
 * result system), so it has a chart/target/value, opens the measurement editor, and reports via the
 * record-value drawer. This is the category-independent measurement signal: clinical measurements
 * (category `measurement`) and lifestyle measurements (category `movement`/`food`/`sleep`/`mind`) are
 * both measurable, while a free-text habit ("walk after dinner") is not.
 */
export function isMeasurableTask(task: Task): boolean {
  return getTaskLoincCode(task) !== null;
}

/** True when a Task is one of the medication category codes. */
export function isMedicationTask(task: Task): boolean {
  const category = getTaskCategory(task);
  return category !== null && MEDICATION_TASK_CATEGORY_CODES.includes(category as MedicationTaskCategoryCode);
}

/**
 * Classify a Task into its care-plan item kind — which drives the edit dialog, remove routing, and
 * AI tool behavior. Measurable tasks (a result code is present) are `'measurement'` regardless of
 * their category, so a lifestyle measurement (category `movement`, with a LOINC) still edits and
 * removes as a measurement. Order matters: measurable is checked before the lifestyle category so a
 * goal-backed lifestyle metric isn't mistaken for a free-text habit.
 */
export function getCarePlanItemKind(task: Task): CarePlanItemKind {
  if (isMeasurableTask(task)) return 'measurement';
  if (isMedicationTask(task)) return 'medication';
  if (isLifestyleTask(task)) return 'lifestyle';
  return 'task';
}

/** Extract the template task ID from an occurrence's partOf reference. Returns null for non-occurrences. */
export function getTemplateTaskId(task: Task): string | null {
  const partOf = task.partOf?.[0];
  if (!partOf?.reference) return null;
  const [resourceType, id] = parseReference(partOf);
  return resourceType === 'Task' ? id : null;
}

/** Derive scheduledDate (YYYY-MM-DD) from executionPeriod.start, falling back to authoredOn. Only applies to occurrences — templates always return null. */
export function getScheduledDate(task: Task): string | null {
  if (task.intent === 'plan') return null;
  const dateSource = task.executionPeriod?.start ?? task.authoredOn;
  if (!dateSource) return null;
  return dateSource.substring(0, 10);
}

/** Derive scheduledWhen (FHIR when code or HH:MM) from the executionPeriod time range. Only applies to occurrences — templates always return null. */
export function getScheduledWhen(task: Task): string | null {
  if (task.intent === 'plan') return null;
  const start = task.executionPeriod?.start;
  const end = task.executionPeriod?.end;
  if (!start || !end) return null;

  const startTime = start.includes('T') ? start.split('T')[1]?.substring(0, 5) : null;
  const endTime = end.includes('T') ? end.split('T')[1]?.substring(0, 5) : null;
  if (!startTime || !endTime) return null;

  if (startTime === endTime && startTime !== '00:00') return startTime;
  if (startTime === '00:00' && endTime === '23:59') return null;

  for (const [code, period] of Object.entries(TIME_PERIODS)) {
    if (startTime === period.start && endTime === period.end) return code;
  }

  return null;
}

/** Extract the SCHEDULED_TIMING extension as a TimingRepeat. */
export function getScheduledTiming(task: Task): TimingRepeat | null {
  const ext = getExtension(task, EXTENSIONS.SCHEDULED_TIMING);
  if (!ext?.extension) return null;

  const repeat: TimingRepeat = {};
  const dayOfWeek: string[] = [];
  const timeOfDay: string[] = [];
  const when: string[] = [];

  for (const sub of ext.extension) {
    switch (sub.url) {
      case EXTENSIONS.TIMING_FREQUENCY:
        repeat.frequency = sub.valueInteger;
        break;
      case EXTENSIONS.TIMING_PERIOD:
        repeat.period = sub.valueInteger;
        break;
      case EXTENSIONS.TIMING_PERIOD_UNIT:
        repeat.periodUnit = sub.valueString as TimingRepeat['periodUnit'];
        break;
      case EXTENSIONS.TIMING_DAY_OF_WEEK:
        if (sub.valueString) dayOfWeek.push(sub.valueString);
        break;
      case EXTENSIONS.TIMING_TIME_OF_DAY:
        if (sub.valueString) timeOfDay.push(sub.valueString);
        break;
      case EXTENSIONS.TIMING_WHEN:
        if (sub.valueString) when.push(sub.valueString);
        break;
      case EXTENSIONS.TIMING_BOUNDS_START:
        if (sub.valueDateTime) {
          repeat.boundsPeriod = { ...repeat.boundsPeriod, start: new Date(sub.valueDateTime) };
        }
        break;
      case EXTENSIONS.TIMING_BOUNDS_END:
        if (sub.valueDateTime) {
          repeat.boundsPeriod = { ...repeat.boundsPeriod, end: new Date(sub.valueDateTime) };
        }
        break;
      default:
        break;
    }
  }

  if (dayOfWeek.length > 0) repeat.dayOfWeek = dayOfWeek;
  if (timeOfDay.length > 0) repeat.timeOfDay = timeOfDay;
  if (when.length > 0) repeat.when = when;

  return repeat;
}

/** True when a Task is marked "as needed" (PRN) via the AS_NEEDED extension. */
export function isAsNeeded(task: Task): boolean {
  return getExtensionValue(task, EXTENSIONS.AS_NEEDED) === true;
}

/**
 * Whether this item is scheduled "Once" — a single occurrence rather than a recurring schedule or an
 * as-needed (PRN) item.
 *
 * Structural, not a stored flag: a Once item carries no `SCHEDULED_TIMING` extension and no
 * `AS_NEEDED` extension. Its due date is optional, so absence of a date does not make it any less a
 * Once item — which is exactly why callers cannot infer the mode from `getDueDate` alone.
 */
export function isOnce(task: Task): boolean {
  return !getScheduledTiming(task) && !isAsNeeded(task);
}

/** Derive dueDate (YYYY-MM-DD) from restriction.period.end. */
export function getDueDate(task: Task): string | null {
  const end = task.restriction?.period?.end;
  if (!end) return null;
  return end.substring(0, 10);
}

/**
 * When a one-off Task was closed — completed, or retracted as entered in error.
 *
 * `executionPeriod.end` is FHIR's "the time final action was taken against the task", and the close
 * paths stamp it in the same patch that writes the status, so the two can never disagree. It is read
 * in preference to `meta.lastUpdated` because that is the last write for *any* reason: editing a
 * closed task's title or posting a comment on it moves `lastUpdated` without changing the status,
 * which would redate the close.
 *
 * `meta.lastUpdated` remains the fallback, for a task closed before the stamp existed. That is an
 * approximation — the same one every reader here used to make — and it degrades in the right
 * direction: only a closed task edited afterwards reads wrong, and only until it is closed again.
 *
 * Only meaningful for a one-off. A recurring occurrence carries its scheduled slot window on
 * `executionPeriod` instead (see {@link getScheduledWhen}), so none of the close paths write it —
 * they all reject occurrences up front.
 */
export function getTaskClosedAt(task: {
  executionPeriod?: { end?: string };
  meta?: { lastUpdated?: string };
}): string | undefined {
  return task.executionPeriod?.end ?? task.meta?.lastUpdated;
}

/**
 * Spanish display name for a task's result code, or null when no validated translation exists.
 * Covers LOINC-coded measurements (labs, vitals) and `survey-scores` coded surveys (Mood).
 * Returns null for English or when the task has no result coding — callers fall back to task.description.
 */
export function getTaskResultDisplayNameEs(task: Task): string | null {
  const coding = task.code?.coding?.find((c) => !!c.system && RESULT_CODE_SYSTEMS.includes(c.system));
  if (!coding?.code) return null;
  // Check both tables by code — survey-scores codes (e.g. 'mood-scale') are stored under the LOINC
  // system on tasks due to how buildTaskBody works, so we can't branch solely on coding.system.
  return LOINC_DISPLAY_NAMES_ES[coding.code] ?? SURVEY_SCORES_DISPLAY_NAMES_ES[coding.code] ?? null;
}

/**
 * True when a Task input entry is the free-text `instructions` field. Other inputs carry machine
 * values (category marker, LOINC config) that must never be treated as translatable free text.
 */
export const isTaskInstructionsInput = (input: NonNullable<Task['input']>[number]): boolean =>
  input.type?.coding?.some((c) => c.system === TASK_INPUT_KEYS_SYSTEM && c.code === 'instructions') ?? false;

/** Extract the instructions from a task's input array. Returns null if not found. */
export function getTaskInstructions(task: Task): string | null {
  return task.input?.find(isTaskInstructionsInput)?.valueString ?? null;
}

/** The FHIR `Quantity.comparator` values a measurement target may carry. There is no `=`: a "meet
 * exactly" target is represented by an omitted comparator, so `=` is intentionally excluded. Single
 * source of truth for the target comparator across services, tRPC schemas, and medplum-config. */
export const TARGET_COMPARATORS = ['>=', '<=', '>', '<'] as const;
export type TargetComparator = (typeof TARGET_COMPARATORS)[number];

/** A quantitative Goal target snapshotted onto a measurement Task. `comparator` is omitted for a
 * "meet exactly" target (the catalog's `=`). */
export interface TaskTarget {
  value: number;
  comparator?: TargetComparator;
  unit: string;
}

/**
 * Extract the quantitative target snapshotted onto a measurement task's `input[target]` (a
 * `Quantity` carrying the comparator). Returns null when the task has no target — i.e. an ordinary
 * measurement/result rather than a lifestyle measurement. The Goal remains the source of truth;
 * this snapshot lets the day-view card render the `Target:` line without loading the Goal.
 */
export function getTaskTarget(task: Task): TaskTarget | null {
  const quantity = task.input?.find((i) =>
    i.type?.coding?.some((c) => c.system === TASK_INPUT_KEYS_SYSTEM && c.code === 'target'),
  )?.valueQuantity;
  if (!quantity || quantity.value === undefined) return null;
  const comparator = quantity.comparator;
  return {
    value: quantity.value,
    comparator:
      comparator === '>=' || comparator === '<=' || comparator === '>' || comparator === '<' ? comparator : undefined,
    unit: quantity.unit ?? '',
  };
}

/** Extract the expected unit from a task's input array. Returns null if not found. */
export function getTaskExpectedUnit(task: Task): string | null {
  return (
    task.input?.find((i) =>
      i.type?.coding?.some((c) => c.system === TASK_INPUT_KEYS_SYSTEM && c.code === 'expected-units'),
    )?.valueString ?? null
  );
}

/**
 * Extract the version-pinned Questionnaire canonical (`url|version`) from a survey task's
 * `input[questionnaire]` entry. Returns null for non-survey tasks. The data-entry mechanism
 * for a survey result is the referenced Questionnaire (see the surveys FHIR design doc).
 */
export function getTaskQuestionnaire(task: Task): string | null {
  return (
    task.input?.find((i) =>
      i.type?.coding?.some((c) => c.system === TASK_INPUT_KEYS_SYSTEM && c.code === 'questionnaire'),
    )?.valueCanonical ?? null
  );
}

/** True when a Task carries a `questionnaire` input — i.e. its result is collected via a survey. */
export function isSurveyTask(task: Task): boolean {
  return getTaskQuestionnaire(task) !== null;
}

/** Check if a task is a recurring template (intent === 'plan'). */
export function isTemplate(task: Task): boolean {
  return task.intent === 'plan';
}

/** Resolve the template ID for a task — returns the task's own ID if it's a template, or the partOf template ID if it's an occurrence. Returns null for standalone tasks. */
export function resolveTemplateId(task: Task): string | null {
  if (task.intent === 'plan') return task.id ?? null;
  return getTemplateTaskId(task);
}
